import { Box, Card, Skeleton, Text } from '@factorialco/deprecated-design-system'
import { Button, IconType } from '@factorialco/factorial-one-react'
import {
  EmojiAvatar,
  StatusTag,
  StatusVariant,
} from '@factorialco/factorial-one-react/dist/experimental'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import type { BenefitListing, BenefitStatus } from 'modules/benefits/lib/benefitListings'

interface ActionProps {
  label: string
  icon?: IconType
  onClick: () => void
}

export interface BenefitsCardBenefitActions {
  primaryAction?: ActionProps
  secondaryAction?: ActionProps
}

interface BenefitsCardBenefitProps {
  benefitListing: BenefitListing
  showTag?: boolean
  actions?: BenefitsCardBenefitActions
}

const BenefitStatusTag = ({ benefitStatus }: { benefitStatus: BenefitStatus }) => {
  const getStatusText = () => {
    switch (benefitStatus) {
      case 'active':
        return i18n.t('benefits.homepage.benefit.active')
      case 'available':
        return i18n.t('benefits.homepage.benefit.available')
      case 'pending':
        return i18n.t('benefits.homepage.benefit.pending')
      default:
        return i18n.t('benefits.homepage.benefit.not_active')
    }
  }

  const getStatusVariant: () => StatusVariant = () => {
    switch (benefitStatus) {
      case 'active':
        return 'positive'
      case 'available':
        return 'info'
      case 'pending':
        return 'info'
      default:
        return 'critical'
    }
  }

  return <StatusTag text={getStatusText()} variant={getStatusVariant()} />
}

const BenefitsCardBenefit = ({
  benefitListing: { id, title, description, benefitStatus, emoji, learnMoreUrl },
  showTag = true,
  actions,
}: BenefitsCardBenefitProps) => {
  const handleClick = () => {
    track(trackingEvents.benefits.openBenefitCardInfo, { benefit_type: id })
  }

  const handlePrimaryAction = () => {
    if (actions?.primaryAction?.onClick) {
      track(trackingEvents.benefits.requestBenefit, { benefit_type: id })
      actions.primaryAction.onClick()
    }
  }

  const showFooter = !!learnMoreUrl?.length || !!actions

  // TODO: Switch to OneCard once it supports EmojiAvatars
  return (
    <Card>
      <Card.Eyebrow>
        <Box width="full" flexDirection="row" justifyContent="spaceBetween">
          <EmojiAvatar emoji={emoji} size="lg" />

          <Box flexDirection="row" justifyContent="spaceBetween" paddingBottom="s8">
            {showTag ? (
              <Box alignItems="center">
                {benefitStatus ? (
                  <BenefitStatusTag benefitStatus={benefitStatus} />
                ) : (
                  <Skeleton width="s80" height="s24" />
                )}
              </Box>
            ) : null}
          </Box>
        </Box>
      </Card.Eyebrow>
      <Card.Content>
        <Box gap="s16" justifyContent="spaceBetween" height="full">
          <Box gap="s2">
            <Text.Normal color="textPrimary" size="300" weight="semibold">
              {title}
            </Text.Normal>

            <Text.Normal color="grey800" size="300">
              {description}
            </Text.Normal>
          </Box>
        </Box>
      </Card.Content>

      {showFooter && (
        <Card.Footer>
          {/*
          This pseudo-footeris an antipattern, but before we switch to OneCard we need to...
          1. Update OneCard support Emoji avatars
          2. Ensure cards are the same height in a row
           */}
          <Box
            flexDirection="row"
            justifyContent="spaceBetween"
            paddingTop="s12"
            paddingX="s16"
            style={{
              flex: '1',
              marginLeft: '-24px',
              marginRight: '-24px',
              marginBottom: '-4px',
            }}
            border={{
              top: {
                width: 's1',
                style: 'solid',
                color: 'grey300',
              },
            }}
          >
            {/* Secondary actions */}
            <Box gap="s8" flexDirection="row">
              {!!learnMoreUrl?.length && (
                <a href={learnMoreUrl} target="_blank" rel="noreferrer" onClick={handleClick}>
                  <Button variant="outline" label={i18n.t('common.learn_more')} />
                </a>
              )}
              {actions?.secondaryAction && (
                <Button
                  label={actions.secondaryAction.label}
                  variant="outline"
                  icon={actions.secondaryAction.icon}
                  onClick={actions.secondaryAction.onClick}
                />
              )}
            </Box>

            {/* Primary action */}
            {actions?.primaryAction && (
              <Button
                label={actions.primaryAction.label}
                onClick={handlePrimaryAction}
                icon={actions.primaryAction.icon}
              />
            )}
          </Box>
        </Card.Footer>
      )}
    </Card>
  )
}

export { BenefitsCardBenefit }
